import {
  FloatingFocusManager,
  FloatingNode,
  FloatingOverlay,
  FloatingPortal,
  useDismiss,
  useFloating,
  useFloatingNodeId,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import { ClockRewind, X } from '@timelyapp/icons/line-icons';
import { Button } from '@timelyapp/react';
import {
  type AgentSessionAudit,
  type AgentSessionAudits,
  getAgentSessionAudits,
} from 'api/agentSessions/agentSessionAudits.api';
import DynamicUserAvatar from 'components/DynamicUserAvatar';
import { UpdateCard } from 'components/ui-blocks/card/UpdateCard';
import { DateTime } from 'luxon';
import React, { useEffect, useRef, useState } from 'react';
import { UserTimeFormat } from 'types/user';
import { readOrInferUserTimeFormat } from 'utils/user';
import dialogStyles from './AgentSessionDetails.module.scss';
import styles from './AgentSessionHistory.module.scss';

function Changes({
  audit,
  projects,
}: {
  audit: AgentSessionAudit;
  projects: AgentSessionAudits['projects'];
}) {
  const projectName = (id: unknown) =>
    id == null
      ? 'Unassigned'
      : (projects.find(project => project.id === id)?.name ?? 'Unavailable project');
  const changes = Object.entries(audit.changes).flatMap(([field, [from, to]]) => {
    switch (field) {
      case 'project_id':
        return [`Changed project from ${projectName(from)} to ${projectName(to)}.`];
      case 'custom_title':
        return [`Changed title from “${from || 'Default title'}” to “${to || 'Default title'}”.`];
      case 'published_at':
        return [to ? 'Published the session.' : 'Unpublished the session.'];
      case 'kept_private':
        return audit.changes.published_at
          ? []
          : [to ? 'Kept the session private.' : 'Removed the keep-private preference.'];
      case 'label_ids': {
        const previous = Array.isArray(from) ? from : [];
        const next = Array.isArray(to) ? to : [];
        const added = next.filter(id => !previous.includes(id)).length;
        const removed = previous.filter(id => !next.includes(id)).length;
        return [
          added ? `Added ${added} ${added === 1 ? 'tag' : 'tags'}.` : '',
          removed ? `Removed ${removed} ${removed === 1 ? 'tag' : 'tags'}.` : '',
        ].filter(Boolean);
      }
      case 'resource_id':
        return [
          to == null
            ? 'Removed linked work.'
            : from == null
              ? 'Added linked work.'
              : 'Changed linked work.',
        ];
      case 'project_assignment_source':
        return audit.changes.project_id
          ? []
          : [
              `Changed assignment source to ${to === 'manual' ? 'manual' : to === 'automatic' ? 'automatic' : to === 'inherited' ? 'inherited' : 'unassigned'}.`,
            ];
      default:
        return ['Updated the session.'];
    }
  });
  return (
    <>
      {changes.map((change, index) => (
        <p key={index}>{change}</p>
      ))}
    </>
  );
}

const reasons: Record<string, string> = {
  suggestion: 'Accepted a project suggestion',
  undo: 'Undid a project suggestion',
  automatic: 'Automatic project assignment',
  upload: 'Assignment inherited from uploaded activity',
};

export function AgentSessionHistoryContent({
  sessionId,
  load = getAgentSessionAudits,
}: {
  sessionId: number;
  load?: typeof getAgentSessionAudits;
}) {
  const [data, setData] = useState<AgentSessionAudits>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [before, setBefore] = useState<number>();
  // biome-ignore lint/correctness/useExhaustiveDependencies: attempt explicitly retries a failed page.
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    load(sessionId, before)
      .then(page => {
        if (!active) return;
        setData(previous =>
          before === undefined || !previous
            ? page
            : {
                ...page,
                audits: [
                  ...previous.audits,
                  ...page.audits.filter(
                    audit => !previous.audits.some(existing => existing.id === audit.id)
                  ),
                ],
                projects: [
                  ...previous.projects,
                  ...page.projects.filter(
                    project => !previous.projects.some(existing => existing.id === project.id)
                  ),
                ],
              }
        );
      })
      .catch(() => {
        if (active) setError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [sessionId, before, attempt, load]);
  const timeFormat =
    readOrInferUserTimeFormat() === UserTimeFormat.H24
      ? DateTime.TIME_24_SIMPLE
      : DateTime.TIME_SIMPLE;
  return (
    <div className={styles.content} aria-busy={loading}>
      {data?.audits.map((audit, index) => {
        const date = DateTime.fromISO(audit.created_at);
        const previousDate = index
          ? DateTime.fromISO(data.audits[index - 1].created_at).toISODate()
          : null;
        const actor =
          audit.user?.name ||
          (audit.comment === 'automatic'
            ? 'Timely'
            : audit.comment === 'upload'
              ? 'Activity upload'
              : 'Unknown user');
        return (
          <React.Fragment key={audit.id}>
            {date.toISODate() !== previousDate && (
              <h3 className={styles.date}>{date.toLocaleString(DateTime.DATE_HUGE)}</h3>
            )}
            <UpdateCard className={styles.entry}>
              {{
                avatar: audit.user ? (
                  <DynamicUserAvatar
                    user={{
                      ...audit.user,
                      id: audit.user.id ?? 0,
                      avatar: audit.user.avatar?.medium_retina,
                    }}
                    size="medium"
                    hasNotification={false}
                    renderNotificationStyles={{ color: '', icon: null }}
                  />
                ) : (
                  <ClockRewind width={22} height={22} />
                ),
                content: (
                  <div className={styles.change}>
                    <strong>{actor}</strong>
                    <Changes audit={audit} projects={data.projects} />
                    {(audit.comment || audit.target === 'periods') && (
                      <p className={styles.context}>
                        {audit.comment ? reasons[audit.comment] : ''}
                        {audit.comment && audit.target === 'periods' ? ' · ' : ''}
                        {audit.target === 'periods'
                          ? `${audit.period_ids.length} ${audit.period_ids.length === 1 ? 'activity period' : 'activity periods'}`
                          : ''}
                      </p>
                    )}
                  </div>
                ),
                actionArea: (
                  <time
                    dateTime={audit.created_at}
                    title={date.toLocaleString(DateTime.DATETIME_FULL)}
                  >
                    {date.toLocaleString(timeFormat)}
                  </time>
                ),
              }}
            </UpdateCard>
          </React.Fragment>
        );
      })}
      {loading && (
        <p className={styles.state} role="status">
          Loading history…
        </p>
      )}
      {error && (
        <div className={styles.state} role="alert">
          <p>Couldn’t load session history.</p>
          <Button onClick={() => setAttempt(value => value + 1)}>Try again</Button>
        </div>
      )}
      {!loading && !error && data?.audits.length === 0 && (
        <p className={styles.state}>There is no history available for this session.</p>
      )}
      {!loading && !error && data?.truncated && data.next_before != null && (
        <div className={styles.more}>
          <Button onClick={() => setBefore(data.next_before!)}>Show more</Button>
        </div>
      )}
    </div>
  );
}

export default function AgentSessionHistory({
  sessionId,
  title,
  children,
  load,
}: {
  sessionId: number;
  title: string;
  children: (open: () => void) => React.ReactElement;
  load?: typeof getAgentSessionAudits;
}) {
  const [open, setOpen] = useState(false);
  const target = useRef<HTMLSpanElement>(null);
  const nodeId = useFloatingNodeId();
  function changeOpen(next: boolean) {
    setOpen(next);
    if (!next) requestAnimationFrame(() => target.current?.querySelector('button')?.focus());
  }
  const { refs, context } = useFloating({ open, onOpenChange: changeOpen, nodeId });
  const { getFloatingProps } = useInteractions([useDismiss(context), useRole(context)]);
  return (
    <FloatingNode id={nodeId}>
      <span ref={target}>{children(() => requestAnimationFrame(() => setOpen(true)))}</span>
      {open && (
        <FloatingPortal>
          <FloatingOverlay lockScroll className={dialogStyles.overlay}>
            <FloatingFocusManager
              context={context}
              initialFocus={refs.floating}
              returnFocus={false}
            >
              <div
                {...getFloatingProps({
                  ref: refs.setFloating,
                  className: styles.dialog,
                  'aria-label': 'Session history',
                  'aria-modal': true,
                })}
              >
                <header className={styles.header}>
                  <div>
                    <h2>Session history</h2>
                    <p>{title}</p>
                  </div>
                  <button
                    type="button"
                    className={dialogStyles.close}
                    aria-label="Close history"
                    onClick={() => changeOpen(false)}
                  >
                    <X width={20} height={20} />
                  </button>
                </header>
                <AgentSessionHistoryContent key={sessionId} sessionId={sessionId} load={load} />
              </div>
            </FloatingFocusManager>
          </FloatingOverlay>
        </FloatingPortal>
      )}
    </FloatingNode>
  );
}
